import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatarDataHora } from "../../utils/dateUtils";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";
import DataGrid from "../../components/DataGrid/index.js";

export default function ClienteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [aba, setAba] = useState("dados");
  const [loading, setLoading] = useState(true);

  const [empresa, setEmpresa] = useState({
    nome: "",
    nomeFantasia: "",
    documento: "",
    tipoPessoa: 1,
    usuariosIds: [],
    gruposIds: [],
  });

  // Controle de envio externo
  const [enviarCertificadoExterno, setEnviarCertificadoExterno] = useState(false);

  // Estado para criação (certificado obrigatório)
  const [certificadoInicial, setCertificadoInicial] = useState({
    arquivo: null,
    senha: "",
    dataValidade: "",
  });

  // Estado para aba de certificados (edição)
  const [certificados, setCertificados] = useState([]);
  const [novoCertificado, setNovoCertificado] = useState({
    arquivo: null,
    senha: "",
    dataValidade: "",
  });
  const [uploadingCertificado, setUploadingCertificado] = useState(false);

  // Modais
  const [modalHistorico, setModalHistorico] = useState({ aberto: false, certificadoId: null });

  const [usuarios, setUsuarios] = useState([]);
  const [grupos, setGrupos] = useState([]);

  useEffect(() => {
    async function carregar() {
      try {
        const [usuariosRes, gruposRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/usuario`),
          axios.get(`${process.env.REACT_APP_API}/grupos`),
        ]);

        let usuariosList = usuariosRes.data.data.items.map((u) => ({
          ...u,
          checked: false,
        }));

        let gruposList = gruposRes.data.data.items.map((g) => ({
          ...g,
          checked: false,
        }));

        if (isEdit) {
          const empresaRes = await axios.get(
            `${process.env.REACT_APP_API}/empresa/${id}`
          );

          const data = empresaRes.data.data;
          const gruposIdsDaEmpresa = data.grupos?.map((g) => g.id) ?? [];

          setEmpresa({
            nome: data.nome,
            nomeFantasia: data.nomeFantasia || "",
            documento: data.documento,
            tipoPessoa: data.tipoPessoa,
            usuariosIds: data.usuariosIds ?? [],
            gruposIds: gruposIdsDaEmpresa,
          });

          setCertificados(data.certificados ?? []);

          usuariosList = usuariosList.map((u) => ({
            ...u,
            checked: data.usuariosIds?.includes(u.id) ?? false,
          }));

          gruposList = gruposList.map((g) => ({
            ...g,
            checked: gruposIdsDaEmpresa.includes(g.id),
          }));
        }

        setUsuarios(usuariosList);
        setGrupos(gruposList);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, [id, isEdit]);

  const toggleUsuario = (userId) => {
    setUsuarios((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, checked: !u.checked } : u
      )
    );
  };

  const toggleGrupo = (grupoId) => {
    setGrupos((prev) =>
      prev.map((g) =>
        g.id === grupoId ? { ...g, checked: !g.checked } : g
      )
    );
  };

  useEffect(() => {
    setEmpresa((prev) => ({
      ...prev,
      usuariosIds: usuarios.filter((u) => u.checked).map((u) => u.id),
    }));
  }, [usuarios]);

  useEffect(() => {
    setEmpresa((prev) => ({
      ...prev,
      gruposIds: grupos.filter((g) => g.checked).map((g) => g.id),
    }));
  }, [grupos]);

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        const payload = { ...empresa };
        await axios.put(`${process.env.REACT_APP_API}/empresa/${id}`, payload)
        .then((res) => {
          AlertComponent("Sucesso", "Cliente salvo com sucesso!", { icon: "success" });
          return navigate(`/clientes?filtro=${encodeURIComponent(empresa.nome)}`);
        })
        .catch((err) => {
          AlertComponent("Erro", "Erro ao salvar cliente", { icon: "error" });
        })
        .finally(() => setLoading(false));
      } else {
        // Validação do certificado apenas se NÃO for envio externo
        if (!enviarCertificadoExterno && !certificadoInicial.arquivo) {
          AlertComponent("Erro", "Certificado é obrigatório para adicionar um cliente", { icon: "error" });
          return;
        }

        const formData = new FormData();
        formData.append("nome", empresa.nome);
        formData.append("nomeFantasia", empresa.nomeFantasia);
        formData.append("documento", empresa.documento);
        formData.append("tipoPessoa", empresa.tipoPessoa);
        formData.append("enviarCertificadoExterno", enviarCertificadoExterno);
        
        // Só envia certificado se NÃO for envio externo
        if (!enviarCertificadoExterno) {
          formData.append("arquivo", certificadoInicial.arquivo);
          formData.append("senhaCertificado", certificadoInicial.senha);
        }
        
        empresa.usuariosIds.forEach((id) => {
          formData.append("usuariosIds", id);
        });

        empresa.gruposIds.forEach((id) => {
          formData.append("gruposIds", id);
        });

        await axios.post(`${process.env.REACT_APP_API}/empresa`, formData)
        .then((res) => {
          AlertComponent("Sucesso", "Empresa criada com sucesso!", { icon: "success" });
          return navigate(`/clientes?filtro=${encodeURIComponent(empresa.nome)}`);
        })
        .catch((err) => {
          AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao criar empresa", { icon: "error" });
        })
        .finally(() => setLoading(false));
      }
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao salvar empresa", { icon: "error" });
    }
  };

  const handleAdicionarCertificado = async () => {
    if (!novoCertificado.arquivo || !novoCertificado.senha) {
      AlertComponent("Erro", "Preencha todos os campos do certificado", { icon: "error" });
      return;
    }

    setUploadingCertificado(true);
    try {
      const formData = new FormData();
      formData.append("idEmpresa", id);
      formData.append("arquivo", novoCertificado.arquivo);
      formData.append("senha", novoCertificado.senha);

      const res = await axios.post(
        `${process.env.REACT_APP_API}/certificadoempresa`,
        formData
      );

      setCertificados((prev) => [...prev, res.data.data]);
      setNovoCertificado({ arquivo: null, senha: "" });
      AlertComponent("Sucesso", "Certificado adicionado com sucesso!", { icon: "success" });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao adicionar certificado", { icon: "error" });
    } finally {
      setUploadingCertificado(false);
    }
  };

  const handleRemoverCertificado = async (certificadoId) => {
    AlertComponent(
            "Confirmação",
            "Confirma a exclusão do certificado? Essa ação é irreversível.",
            { icon: "question", showCancelButton: true, confirmButtonText: "Sim" },
            async (result) => {
                if (result.isConfirmed) {
                    try {
                      await axios.delete(`${process.env.REACT_APP_API}/certificadoempresa/${certificadoId}`);
                      setCertificados((prev) => prev.filter((c) => c.id !== certificadoId));
                      AlertComponent("Sucesso", "Certificado removido com sucesso!", { icon: "success" });
                    } catch (err) {
                      console.error(err);
                      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao remover certificado", { icon: "error" });
                    }
                }
            }, false
        );
  };

  const handleAbrirHistorico = (certificadoId) => {
    setModalHistorico({ aberto: true, certificadoId });
  };

  const getStatusCertificado = (dataValidade) => {
    const hoje = new Date();
    const validade = new Date(dataValidade);
    const diasRestantes = Math.ceil((validade - hoje) / (1000 * 60 * 60 * 24));

    if (diasRestantes < 0) {
      return { label: "Expirado", className: "bg-red-100 text-red-700" };
    } else if (diasRestantes <= 30) {
      return { label: `Expira em ${diasRestantes} dias`, className: "bg-yellow-100 text-yellow-700" };
    } else {
      return { label: "Válido", className: "bg-green-100 text-green-700" };
    }
  };

  const formatarCPF = (valor) => {
    return valor
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const formatarCNPJ = (valor) => {
    return valor
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const formatarDocumento = (valor, tipoPessoa) => {
    if (tipoPessoa === 0 || tipoPessoa === "0") {
      return formatarCPF(valor);
    }
    return formatarCNPJ(valor);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "documento") {
      setEmpresa((prev) => ({
        ...prev,
        [name]: formatarDocumento(value, prev.tipoPessoa),
      }));
    } else if (name === "tipoPessoa") {
      setEmpresa((prev) => ({
        ...prev,
        [name]: parseInt(value),
        documento: "",
      }));
    } else {
      setEmpresa((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Colunas do histórico
  const colunasHistorico = [
    {
      field: "dataHoraUtilizacaoUtc",
      header: "Data/Hora",
      sortable: true,
      render: (row) => formatarDataHora(row.dataHoraUtilizacaoUtc)
    },
    {
      field: "usuario",
      header: "Usuário",
      sortable: true,
    },
    {
      field: "ip",
      header: "IP",
      sortable: false,
    },
    {
      field: "url",
      header: "URL",
      sortable: false,
    }
  ];

  if (loading) return <Loading />;

  return (
    <div className="w-full">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Cliente" : "Novo Cliente"}
        </h1>
        <p className="text-sm text-slate-500">
          Gerencie os dados da empresa e defina quais usuários terão acesso
        </p>
      </div>

      {/* ABAS */}
      <div className="mb-8 border-b">
        <nav className="flex gap-6">
          {[
            { id: "dados", label: "Dados do Cliente" },
            { id: "usuarios", label: "Usuários com Acesso" },
            { id: "grupos", label: "Grupos" },
            ...(isEdit ? [{ id: "certificados", label: "Certificados" }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setAba(tab.id)}
              className={`pb-3 text-sm font-medium transition ${
                aba === tab.id
                  ? "border-b-2 border-slate-900 text-slate-900"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab.label}
              {tab.id === "certificados" && certificados.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {certificados.length}
                </span>
              )}
              {tab.id === "usuarios" && usuarios.filter(u => u.checked).length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {usuarios.filter(u => u.checked).length}
                </span>
              )}
              {tab.id === "grupos" && grupos.filter(g => g.checked).length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {grupos.filter(g => g.checked).length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ABA DADOS */}
      {aba === "dados" && (
        <div className="bg-white p-8 rounded-xl shadow border w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Pessoa</label>
              <select
                name="tipoPessoa"
                value={empresa.tipoPessoa}
                onChange={handleChange}
                className="w-full p-3 border rounded-md"
              >
                <option value={0}>Pessoa Física</option>
                <option value={1}>Pessoa Jurídica</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {empresa.tipoPessoa === 1 ? "CNPJ" : "CPF"}
              </label>
              <input
                name="documento"
                value={empresa.documento}
                onChange={handleChange}
                className="w-full p-3 border rounded-md"
                placeholder={empresa.tipoPessoa === 1 ? "00.000.000/0000-00" : "000.000.000-00"}
                maxLength={empresa.tipoPessoa === 1 ? 18 : 14}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                name="nome"
                value={empresa.nome}
                onChange={handleChange}
                className="w-full p-3 border rounded-md"
                placeholder="Nome do cliente"
              />
            </div>

            {empresa.tipoPessoa === 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Fantasia</label>
                <input
                  name="nomeFantasia"
                  value={empresa.nomeFantasia}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md"
                  placeholder="Nome fantasia"
                />
              </div>
            )}

            

            {!isEdit && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Solicitar certificado via link externo?
                  </label>
                  <select
                    value={enviarCertificadoExterno ? "sim" : "nao"}
                    onChange={(e) => {
                      const valor = e.target.value === "sim";
                      setEnviarCertificadoExterno(valor);
                      if (valor) {
                        setCertificadoInicial({ arquivo: null, senha: "", dataValidade: "" });
                      }
                    }}
                    className="w-full p-3 border rounded-md"
                  >
                    <option value="nao">Não - Enviar o certificado agora</option>
                    <option value="sim">Sim - Enviar link para o cliente</option>
                  </select>
                </div>

                {!enviarCertificadoExterno && (
                  <>
                    <div className="md:col-span-2">
                      <hr className="my-4" />
                      <h3 className="text-lg font-medium text-gray-800 mb-4">Certificado Digital</h3>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Arquivo do Certificado *
                      </label>
                      <input
                        type="file"
                        accept=".pfx,.p12"
                        onChange={(e) =>
                          setCertificadoInicial((prev) => ({
                            ...prev,
                            arquivo: e.target.files?.[0] || null,
                          }))
                        }
                        className="w-full p-2 border rounded-md"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senha do Certificado *
                      </label>
                      <input
                        type="password"
                        value={certificadoInicial.senha}
                        onChange={(e) =>
                          setCertificadoInicial((prev) => ({ ...prev, senha: e.target.value }))
                        }
                        className="w-full p-3 border rounded-md"
                        placeholder="Senha"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ABA USUÁRIOS */}
      {aba === "usuarios" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <div className="space-y-3">
            {usuarios.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhum usuário cadastrado</p>
            ) : (
              usuarios.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer"
                >
                  <span className="font-medium text-slate-700">{u.nome}</span>
                  <input
                    type="checkbox"
                    checked={u.checked}
                    onChange={() => toggleUsuario(u.id)}
                    className="w-4 h-4"
                  />
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* ABA GRUPOS */}
      {aba === "grupos" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <div className="space-y-3">
            {grupos.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhum grupo cadastrado</p>
            ) : (
              grupos.map((g) => (
                <label
                  key={g.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer"
                >
                  <span className="font-medium text-slate-700">{g.nome}</span>
                  <input
                    type="checkbox"
                    checked={g.checked}
                    onChange={() => toggleGrupo(g.id)}
                    className="w-4 h-4"
                  />
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* ABA CERTIFICADOS (apenas edição) */}
      {aba === "certificados" && isEdit && (
        <div className="space-y-6">
          {/* Formulário para adicionar novo certificado */}
          <div className="bg-white p-6 rounded-xl shadow border">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Adicionar Novo Certificado</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo</label>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(e) =>
                    setNovoCertificado((prev) => ({
                      ...prev,
                      arquivo: e.target.files?.[0] || null,
                    }))
                  }
                  className="w-full p-2 border rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <input
                  type="password"
                  value={novoCertificado.senha}
                  onChange={(e) =>
                    setNovoCertificado((prev) => ({ ...prev, senha: e.target.value }))
                  }
                  className="w-full p-2 border rounded-md"
                  placeholder="Senha do certificado"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleAdicionarCertificado}
                  disabled={uploadingCertificado}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
                >
                  {uploadingCertificado ? "Enviando..." : "Adicionar"}
                </button>
              </div>
            </div>
          </div>

          {/* Lista de certificados */}
          <div className="bg-white p-6 rounded-xl shadow border">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Certificados Cadastrados ({certificados.length})
            </h3>

            {certificados.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Nenhum certificado cadastrado para este cliente.
              </p>
            ) : (
              <div className="space-y-3">
                {certificados.map((cert) => {
                  const status = getStatusCertificado(cert.dataValidade);
                  return (
                    <div
                      key={cert.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <svg
                            className="w-6 h-6 text-slate-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {cert.nomeArquivo || `Certificado #${cert.id}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            Validade: {new Date(cert.dataValidade).toLocaleDateString("pt-BR")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${status.className}`}>
                          {status.label}
                        </span>
                        
                        {/* Botão Histórico */}
                        <button
                          onClick={() => handleAbrirHistorico(cert.id)}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-md"
                          title="Ver detalhes"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>

                        {/* Botão Remover */}
                        <button
                          onClick={() => handleRemoverCertificado(cert.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                          title="Remover certificado"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AÇÕES */}
      <div className="mt-10 flex justify-end gap-3 border-t pt-6">
        <button
          onClick={() => navigate("/clientes")}
          className="px-5 py-2 rounded-md border hover:bg-gray-50"
        >
          Cancelar
        </button>

        <button
          onClick={handleSubmit}
          className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800"
        >
          {isEdit ? "Salvar alterações" : "Cadastrar Cliente"}
        </button>
      </div>

      {/* MODAL HISTÓRICO COM DATAGRID */}
      {modalHistorico.aberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Histórico de Acessos
                </h3>
                <p className="text-sm text-gray-500">
                  Certificado #{modalHistorico.certificadoId}
                </p>
              </div>
              <button
                onClick={() => setModalHistorico({ aberto: false, certificadoId: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-auto flex-1">
              <DataGrid
                permitirAlterarQtdPerPage={false}
                endpoint={`${process.env.REACT_APP_API}/certificadoempresa/${modalHistorico.certificadoId}/historico`}
                columns={colunasHistorico}
                emptyMessage="Nenhum acesso registrado para este certificado"
                rowKey="id"
              />
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl shrink-0">
              <button
                onClick={() => setModalHistorico({ aberto: false, certificadoId: null })}
                className="w-full px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}